import { useMutation } from "@tanstack/react-query";
import * as React from "react";

import {
  ApiError,
  createDealDraft,
  updateDealDraft,
  type Deal,
  type DealDraftCreatePayload,
  type IndustryVertical,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormError } from "@/components/ui/form-error";

import { decode422, highlightFieldFor, joinName, splitName } from "./deal422";

/** Partner-org + end-customer-domain values that ride along with the
 * client-details create/update. CHP-339 removed the dedicated identity step
 * that used to collect them; the org name now seeds from the registering
 * partner's profile (or the on-behalf owner's organisation) and the domain
 * is no longer collected at registration time. The shape is retained so a
 * resumed draft's existing values still flow through the create payload. */
export interface DealIdentityValues {
  partnerOrgName: string;
  clientDomain: string;
}

interface Props {
  /** The draft so far, if one already exists (back-nav / resume). */
  deal: Deal | null;
  /** Step-1 identity values — persisted with this step's create/update. */
  identity: DealIdentityValues;
  /** CHP-202 on-behalf: the owning partner's user id, sent on CREATE only
   * (the update payload rejects unknown fields). CHP-368: the manager picks
   * this on the wizard's separate "Assign Deal Owner" step now, so this is
   * always supplied externally — the picker no longer lives inside this step. */
  targetBdmId?: string;
  /** Position in the wizard (CHP-368: 2 when an Assign-Deal-Owner step precedes
   * it for the manager on-behalf flow; 1 otherwise). Drives the card heading. */
  stepNumber?: number;
  /** CHP-474 cross-step jump: a server field name to highlight on mount, set by
   * the wizard when a later step's submit failed validation on a Client-details
   * field (so the user lands on the offending input). */
  initialErrorField?: string | null;
  onComplete: (deal: Deal) => void;
  /** CHP-1014: renders a Cancel button in the footer (where later steps put
   * Back). The wizard owns the discard confirmation — this just opens it. */
  onCancel?: () => void;
}

const INDUSTRY_OPTIONS: { value: IndustryVertical; label: string }[] = [
  { value: "healthcare", label: "Healthcare" },
  { value: "finance", label: "Finance" },
  { value: "utilities", label: "Utilities" },
  { value: "logistics", label: "Logistics" },
  { value: "government", label: "Government" },
  { value: "technology", label: "Technology" },
  { value: "retail", label: "Retail" },
];

function isValidAbn(value: string): boolean {
  return /^\d{11}$/.test(value);
}

// CHP-781: form (DOM) order of the highlightable fields, so when several error
// at once we scroll/focus the FIRST one top-to-bottom.
const FIELD_FOCUS_ORDER = [
  "client_name",
  "client_abn",
  "lead_first_name",
  "client_lead_contact",
  "industry_vertical",
] as const;

export function DealWizardStep1({
  deal,
  identity,
  targetBdmId,
  stepNumber = 1,
  initialErrorField = null,
  onComplete,
  onCancel,
}: Props) {
  const { accessToken } = useCurrentUser();
  // Review-r2 (cad3edb9): Client name is a single field again; the LEAD CONTACT
  // is now captured as First Name + Surname, split from the stored single
  // client_lead_contact value on resume and recombined on submit.
  const [clientName, setClientName] = React.useState(deal?.client_name ?? "");
  const [clientAbn, setClientAbn] = React.useState(deal?.client_abn ?? "");
  const initialLead = splitName(deal?.client_lead_contact);
  const [leadFirstName, setLeadFirstName] = React.useState(initialLead.first);
  const [leadSurname, setLeadSurname] = React.useState(initialLead.surname);
  const [industryVertical, setIndustryVertical] = React.useState<IndustryVertical | "">(
    deal?.industry_vertical ?? "",
  );
  const [error, setError] = React.useState<string | null>(null);
  // CHP-781: every invalid field at once, keyed by the highlight field name ->
  // its inline message, so submit can flag ALL missing required fields
  // simultaneously instead of one-at-a-time. CHP-474: aria-invalid + scroll/focus
  // the FIRST errored field. Seeded from a cross-step jump (a later step's submit
  // named a Client field).
  const [errorFields, setErrorFields] = React.useState<Record<string, string>>(() => {
    const seeded = highlightFieldFor(initialErrorField);
    return seeded ? { [seeded]: "" } : {};
  });

  // Refs for the fields we scroll-into-view + focus when one errors. The order
  // here is the form (DOM) order, so CHP-781 focuses the FIRST invalid field.
  const clientNameRef = React.useRef<HTMLInputElement>(null);
  const abnRef = React.useRef<HTMLInputElement>(null);
  const leadFirstNameRef = React.useRef<HTMLInputElement>(null);
  const industryRef = React.useRef<HTMLSelectElement>(null);

  // CHP-474/781: when fields error, scroll the FIRST (top-to-bottom) into view
  // and focus it so the user is taken straight to what they need to fix (idiom
  // from DealDashboardHeader). The remaining invalid fields stay highlighted.
  React.useEffect(() => {
    const firstField = FIELD_FOCUS_ORDER.find((f) => f in errorFields);
    if (!firstField) return;
    const el =
      firstField === "client_name"
        ? clientNameRef.current
        : firstField === "client_abn"
          ? abnRef.current
          : // Review-r2 (cad3edb9): lead contact 422 highlights the first-name input.
            firstField === "lead_first_name" || firstField === "client_lead_contact"
            ? leadFirstNameRef.current
            : firstField === "industry_vertical"
              ? industryRef.current
              : null;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => el.focus(), 150);
  }, [errorFields]);

  // Create-or-update: the first save POSTs a new draft; any return to this
  // step (back-nav or resume) PATCHes the existing draft so later-step data
  // is never orphaned. The step-1 identity values (partner org + end
  // customer domain) ride along on both paths — a draft can't exist
  // without a client_name, so this is their first persistence point.
  const save = useMutation<Deal, ApiError, DealDraftCreatePayload>({
    mutationFn: (payload) =>
      deal
        ? updateDealDraft(accessToken!, deal.id, payload)
        : createDealDraft(accessToken!, { ...payload, target_bdm_id: targetBdmId }),
    onSuccess: (saved) => {
      onComplete(saved);
    },
    onError: (e) => {
      const detail =
        e.body && typeof e.body === "object" && "detail" in e.body
          ? (e.body as { detail?: unknown }).detail
          : undefined;
      if (e.status === 422 && detail === "target owner has not completed compulsory training") {
        // CHP-202 hard block: the selected owner is uncertified. Advisory
        // copy only — the server already refused the registration.
        setErrorFields({});
        setError(
          "This deal owner has not completed their compulsory training. " +
            "Deals cannot be registered on their behalf until they are certified — " +
            "go back and choose a different owner.",
        );
      } else if (e.status === 422 && detail === "target must be an active channel partner") {
        setErrorFields({});
        setError(
          "The selected deal owner is not an active channel partner. " +
            "Go back and choose a different owner.",
        );
      } else if (e.status === 422) {
        // Decode the validation error so the user learns the REAL reason
        // instead of a blanket "check all fields", AND learns WHICH field is
        // wrong (CHP-474). A 422 here is almost always a Pydantic field error
        // (detail = a list of error objects) or a hand-thrown server check
        // (detail = a string). When the offending field is one we no longer
        // expose in the wizard (partner_org_name seeds off-screen from the
        // profile; target_bdm_id is picked on the Assign step), name it AND
        // route the user to where they can act, since the form below can't fix
        // it. A client_lead_contact 422 highlights the lead First Name input.
        const decoded = decode422(detail);
        setError(decoded.message);
        const hl = highlightFieldFor(decoded.field);
        setErrorFields(hl ? { [hl]: decoded.message } : {});
      } else if (e.status === 403 && detail === "training_required") {
        // RACCPE-22 lockout — usually caught earlier by the route guard, but
        // handle a stale client here too rather than showing a generic error.
        setErrorFields({});
        setError("Finish your outstanding compulsory training before registering deals.");
      } else if (e.status === 403) {
        setErrorFields({});
        setError("Your account isn't allowed to create deals.");
      } else {
        setErrorFields({});
        setError("Couldn't save progress. Please try again.");
      }
    },
  });

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setErrorFields({});

    // CHP-781: validate EVERY required field and collect all failures, so the
    // user sees every missing/invalid field highlighted at once on submit
    // instead of fixing-and-resubmitting one at a time. The first failure (in
    // DOM order) also drives the top-level summary message + the scroll/focus.
    const fieldErrors: Record<string, string> = {};

    // Review-r2 (cad3edb9): Client name is a single required field.
    const trimmedName = clientName.trim();
    if (!trimmedName) {
      fieldErrors.client_name = "Client name is required.";
    }

    const trimmedAbn = clientAbn.trim();
    if (!trimmedAbn) {
      fieldErrors.client_abn = "Client ABN is required.";
    } else if (!isValidAbn(trimmedAbn)) {
      fieldErrors.client_abn = "ABN must be exactly 11 digits.";
    }

    // Review-r2 (cad3edb9): lead contact First Name required; Surname optional.
    const trimmedLeadFirst = leadFirstName.trim();
    if (!trimmedLeadFirst) {
      fieldErrors.lead_first_name = "Lead contact first name is required.";
    }

    if (!industryVertical) {
      fieldErrors.industry_vertical = "Industry vertical is required.";
    }

    if (Object.keys(fieldErrors).length > 0) {
      setErrorFields(fieldErrors);
      // Top-level summary names the FIRST (DOM-order) failure so the alert
      // reads sensibly while every field below stays highlighted.
      const firstField = FIELD_FOCUS_ORDER.find((f) => f in fieldErrors);
      setError(firstField ? (fieldErrors[firstField] ?? null) : null);
      return;
    }

    // No field errors → industryVertical is set (the check above guarantees it);
    // narrow away the "" union member for the type-checker.
    if (!industryVertical) return;

    // Combine the lead contact into the single client_lead_contact column.
    const combinedLeadContact = joinName(leadFirstName, leadSurname);

    const payload: DealDraftCreatePayload = {
      client_name: trimmedName,
      client_abn: trimmedAbn,
      client_lead_contact: combinedLeadContact,
      industry_vertical: industryVertical,
    };
    // CHP-339 removed the identity step, so partner_org_name is no longer
    // user-editable — it auto-seeds off-screen from the owner's profile/org.
    // We deliberately STOP forwarding it: the server stores it verbatim
    // (deal_workflow.create_draft) with a 1-200 char constraint, so a long or
    // empty seeded org name would 422 a registration the user has no way to
    // fix. The authoritative org name on every manager/list view derives from
    // the live organisation FK (channel_partner_company), not this snapshot
    // column, so omitting it loses nothing — and a resumed draft that already
    // persisted a value keeps it (PATCH only writes supplied fields).
    if (identity.clientDomain) payload.client_domain = identity.clientDomain;

    save.mutate(payload);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Step {stepNumber}: Client details</CardTitle>
      </CardHeader>
      <CardContent>
        {/* CHP-781: noValidate so OUR JS validation runs on submit and flags
            EVERY missing required field at once — native HTML validation would
            block submission on the first empty required field instead. */}
        <form noValidate onSubmit={onSubmit} className="grid gap-4">
          {/* Review-r2 (cad3edb9): Client name is a single field. */}
          <div className="grid gap-2">
            <Label htmlFor="wiz-client-name">Client name *</Label>
            <Input
              id="wiz-client-name"
              ref={clientNameRef}
              required
              minLength={1}
              maxLength={200}
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              aria-invalid={"client_name" in errorFields}
              aria-describedby={"client_name" in errorFields ? "wiz-client-name-error" : undefined}
              placeholder="e.g. Coles Group"
            />
            <FormError id="wiz-client-name-error" inline>
              {errorFields.client_name}
            </FormError>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="wiz-client-abn">Client ABN *</Label>
            <Input
              id="wiz-client-abn"
              ref={abnRef}
              maxLength={11}
              inputMode="numeric"
              pattern="\d{11}"
              value={clientAbn}
              onChange={(e) => setClientAbn(e.target.value.replace(/\D/g, ""))}
              aria-invalid={"client_abn" in errorFields}
              aria-describedby={"client_abn" in errorFields ? "wiz-client-abn-error" : undefined}
              placeholder="e.g. 12345678901"
            />
            <p className="text-xs text-muted-foreground">11-digit Australian Business Number</p>
            <FormError id="wiz-client-abn-error" inline>
              {errorFields.client_abn}
            </FormError>
          </div>

          {/* Review-r2 (cad3edb9): lead contact captured as First Name + Surname;
              combined into the single client_lead_contact column on submit. */}
          <div className="grid gap-2">
            <Label>Lead contact</Label>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="wiz-lead-first-name" className="text-xs font-normal">
                  First name *
                </Label>
                {/* First name (100) + a joining space + Surname must stay within
                    the server's 200-char client_lead_contact cap (schemas/deal.py),
                    so the surname is capped at 99 (100 + 1 + 99 = 200). */}
                <Input
                  id="wiz-lead-first-name"
                  ref={leadFirstNameRef}
                  maxLength={100}
                  value={leadFirstName}
                  onChange={(e) => setLeadFirstName(e.target.value)}
                  aria-invalid={"lead_first_name" in errorFields}
                  aria-describedby={
                    "lead_first_name" in errorFields ? "wiz-lead-first-name-error" : undefined
                  }
                  placeholder="e.g. Jane"
                />
                <FormError id="wiz-lead-first-name-error" inline>
                  {errorFields.lead_first_name}
                </FormError>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="wiz-lead-surname" className="text-xs font-normal">
                  Surname
                </Label>
                <Input
                  id="wiz-lead-surname"
                  maxLength={99}
                  value={leadSurname}
                  onChange={(e) => setLeadSurname(e.target.value)}
                  placeholder="e.g. Smith"
                />
              </div>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="wiz-industry">Industry vertical *</Label>
            <select
              id="wiz-industry"
              ref={industryRef}
              value={industryVertical}
              onChange={(e) => setIndustryVertical(e.target.value as IndustryVertical | "")}
              aria-invalid={"industry_vertical" in errorFields}
              aria-describedby={
                "industry_vertical" in errorFields ? "wiz-industry-error" : undefined
              }
              className="h-9 rounded-md border border-input bg-background px-3 text-sm aria-invalid:border-destructive aria-invalid:ring-1 aria-invalid:ring-destructive/20"
            >
              <option value="">-- Select industry --</option>
              {INDUSTRY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <FormError id="wiz-industry-error" inline>
              {errorFields.industry_vertical}
            </FormError>
          </div>

          {/* CHP-781: top-level summary names the first failure (or a server-level
              error like a training block); per-field errors above flag every
              other missing field at once. */}
          {error ? <FormError>{error}</FormError> : null}

          {/* CHP-1014: Cancel sits where later steps put Back; it opens the
              wizard's discard confirmation rather than navigating directly. */}
          <div className={onCancel ? "flex justify-between" : "flex justify-end"}>
            {onCancel ? (
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            ) : null}
            <Button type="submit" loading={save.isPending} loadingLabel="Saving…">
              Next
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
